import React, { createContext, useEffect, useState } from "react"

export interface DeviceContextProps {
  isMobile: boolean
  isTouchDevice: boolean
  viewportWidth: number
  viewportHeight: number
  isPortrait: boolean
  cookiesEnabled: boolean
  screenDensity: number
  isKeyboardOpen: boolean
  platform: "mac" | "win" | "linux"
}

export const DeviceContext = createContext<DeviceContextProps>({
  isMobile: false,
  isTouchDevice: false,
  viewportWidth: 0,
  viewportHeight: 0,
  isPortrait: false,
  cookiesEnabled: false,
  screenDensity: 1,
  isKeyboardOpen: false,
  platform: "mac",
})

export const KEYS = {
  openFinder: { mac: "cmd+k", win: "ctrl+k", linux: "ctrl+k" },
  openProjectFinder: {
    mac: "cmd+shift+k",
    win: "ctrl+shift+k",
    linux: "ctrl+shift+k",
  },
  undo: { mac: "cmd+z", win: "ctrl+z", linux: "ctrl+z" },
  redo: { mac: "cmd+shift+z", win: "ctrl+y", linux: "ctrl+shift+z" },
  navigateHistoryGlob: {
    mac: "cmd+shift+*",
    win: "ctrl+shift+*",
    linux: "ctrl+shift+*",
  },
  lassoingEventKey: { mac: "metaKey", win: "ctrlKey", linux: "ctrlKey" },
}

export type PlatformTag = "mac" | "win" | "linux"

export const detectPlatform = (): PlatformTag => {
  const platform =
    window.navigator.platform.indexOf("Win") != -1
      ? "win"
      : window.navigator.platform.indexOf("Mac") != -1
      ? "mac"
      : window.navigator.platform.indexOf("Linux") != -1
      ? "linux"
      : "linux"

  return platform
}

export const DeviceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceContextProps>({
    isMobile: false,
    isTouchDevice: false,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    isPortrait: false,
    cookiesEnabled: navigator.cookieEnabled,
    screenDensity: window.devicePixelRatio,
    isKeyboardOpen: false,
    platform: detectPlatform(),
  })

  const handleResize = () => {
    setDeviceInfo((prev) => ({
      ...prev,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    }))
  }

  const handleKeyboardOpen = () => {
    setDeviceInfo((prev) => ({
      ...prev,
      isKeyboardOpen: true,
    }))
  }

  const handleKeyboardClose = () => {
    setDeviceInfo((prev) => ({
      ...prev,
      isKeyboardOpen: false,
    }))
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0
    const isPortraitMode = window.matchMedia("(orientation: portrait)").matches

    setDeviceInfo((prev) => ({
      ...prev,
      isMobile: isMobileDevice,
      isTouchDevice: isTouch,
      isPortrait: isPortraitMode,
    }))

    window.addEventListener("resize", handleResize)
    window.addEventListener("focusin", handleKeyboardOpen)
    window.addEventListener("focusout", handleKeyboardClose)

    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("focusin", handleKeyboardOpen)
      window.removeEventListener("focusout", handleKeyboardClose)
    }
  }, [])

  return (
    <DeviceContext.Provider value={deviceInfo}>
      {children}
    </DeviceContext.Provider>
  )
}
